import React, {useEffect, useState} from 'react';
import {Image, Center, VStack, Box, useTheme, Container, HStack, Flex} from '@chakra-ui/react';
import {H2, TextG14, TextA18, TextG12, TextG10} from '../../../../components/typography';
import {BlueButton, BlueButtonFixedWidth, WhiteButton} from '../../../../components/button';
import {Checkbox} from '../../../../components/checkbox';
import {useNavigate, useSearchParams} from 'react-router-dom';
import useAuth from '../../../../hooks/useAuth';
import {updateUser, completeRegistration} from '../../../../endpoints/api';
import {PageLoader} from '../../../../components/PageLoader';
import {getAppName} from '../../../../utils/app';
import {ToastMessage} from '../../../../components/toast';
import WelcomeImage from '../../../../assets/images/welcome-image.png';
import Logo from '../../../../assets/logos/logo_120x120.png';
import {
  PointsDistributionTypes,
  usePointsDistribution,
} from '../../../../hooks/usePointsDistribution';
import {isMobile, isStandalone} from '../../../../utils/deviceCheck';
import RequiresEarlyAccess from '../../../../components/RequiresEarlyAccess';
import {logAnalyticsEvent} from '../../../../utils/analytics';

const Welcome = () => {
  const {user, reFetchUserData} = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState(true);
  const {getPointAmount} = usePointsDistribution();
  const welcomePoints = getPointAmount(PointsDistributionTypes.REGISTRATION_COMPLETED);

  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('membershipPreview');

  const handleOnContinueClick = () => {
    updateUser({sms_enabled: checked}).then(() => {
      setIsLoading(true);
      completeRegistration()
        .then(() =>
          reFetchUserData()
            .catch((e) =>
              ToastMessage({
                status: 'error',
                text: e.message,
              })
            )
            .finally(() =>
              isMobile || window['ReactNativeWebView'] ? navigate('/home') : navigate('/download')
            )
        )
        .catch((e) => console.log(e))
        .finally(() =>
          isMobile || window['ReactNativeWebView'] ? navigate('/home') : navigate('/download')
        );
    });
  };

  const handleOnMembershipsClick = () => {
    updateUser({sms_enabled: checked}).then(() => {
      setIsLoading(true);
      completeRegistration()
        .then(() =>
          reFetchUserData()
            .catch((e) =>
              ToastMessage({
                status: 'error',
                text: e.message,
              })
            )
            .finally(() =>
              isMobile || window['ReactNativeWebView']
                ? navigate('/memberships')
                : navigate('/download')
            )
        )
        .catch((e) => console.log(e))
        .finally(() =>
          isMobile || window['ReactNativeWebView']
            ? navigate('/memberships')
            : navigate('/download')
        );
    });
  };

  useEffect(() => {
    reFetchUserData();
  }, []);

  if (isLoading) return <PageLoader />;

  if (!user) {
    return <TextG14>Something went wrong</TextG14>;
  }

  if (user.hasActiveMembership) {
    navigate(`/membership/${user.activeMembershipPlan.id}?success=1`);

    return null;
  }

  return (
    <Container px={8}>
      <VStack spacing={3} mb={5} mt={5} textAlign="center">
        <Center>
          <Image px="150px" h={'100%'} paddingInline={0} src={Logo} width="61px" alt="Logo" />
        </Center>
        <H2>You’re in! Now Let’s Get You Plugged In.</H2>
      </VStack>
      <VStack spacing="15px">
        <Center>
          <Image borderRadius="8px" h={'100%'} src={WelcomeImage} width="100%" alt="Welcome" />
        </Center>
      </VStack>
      <TextG14 color={theme.colors.grey} my={5} lineHeight="19.95px">
        Right now, you’re in with a basic Walkabout membership. But if you really want to get the
        most out of your neighborhood, our <b style={{color: theme.colors.rose}}>Insiders</b> get:
        <ul style={{marginLeft: '20px', marginTop: '1rem'}}>
          <li>Rotating monthly perks in town worth $110+</li>
          <li>Invites to exclusive meet-ups and pop up experiences</li>
          <li>4x Points Boosts</li>
          <li>And so much more</li>
        </ul>
      </TextG14>
      <Center w="100%">
        <Flex w="100%" flexDir="column" gap="20px">
          {isPreview ? (
            <>
              <BlueButton w="223px" onClick={() => {
                logAnalyticsEvent('select_content', {
                  content_type: 'cta',
                  item_id: 'Continue',
                  page: 'Welcome',
                  destination_page: isMobile || window['ReactNativeWebView'] ? 'Memberships' : 'Download',
                });
                handleOnMembershipsClick();
              }}>
                <TextG12>Continue</TextG12>
              </BlueButton>
            </>
          ) : (
            <>
              <BlueButton w="100%" onClick={() => {
                logAnalyticsEvent('select_content', {
                  content_type: 'cta',
                  item_id: 'See My Options',
                  page: 'Welcome',
                  destination_page: isMobile || window['ReactNativeWebView'] ? 'Memberships' : 'Download',
                });
                handleOnMembershipsClick();
              }}>
                <TextG12>See My Options</TextG12>
              </BlueButton>
              <WhiteButton
                color={theme.colors.grey}
                w="100%"
                border="transparent"
                onClick={handleOnContinueClick}>
                Continue with Basic
              </WhiteButton>
            </>
          )}
        </Flex>
      </Center>
      <Center>
        <HStack spacing={2} py="20px" alignItems="center">
          <Box>
            <Checkbox
              isCircle={false}
              onChange={() => setChecked(!checked)}
              checked={checked}
              disabled={false}
              styled
            />
          </Box>
          <TextG14 color={theme.colors.lightBlack} fontWeight="500">
            Send me SMS
          </TextG14>
        </HStack>
      </Center>
      <Center>
        <TextG12 color={theme.colors.grey} textAlign="center" lineHeight="17.1px">
          We send you personalized invites and perks — you can opt out at any time.
        </TextG12>
      </Center>
    </Container>
  );
};

export default Welcome;
